import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Plus, Edit, Trash2, Tag } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [name, setName] = useState('');
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const getToken = () => localStorage.getItem('admin_token');
  const authHeaders = () => ({ 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' });

  const checkAuth = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/auth/me`, { headers: { 'Authorization': `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error();
    } catch { navigate('/admin'); }
  }, [navigate]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const fetchCats = async () => {
    const res = await fetch(`${API}/api/categories`);
    setCategories(await res.json());
  };

  useEffect(() => { fetchCats(); }, []);

  const handleSave = async () => {
    if (!name.trim()) { setError('Ime kategorije je obvezno'); return; }
    setError('');
    if (editId) {
      await fetch(`${API}/api/categories/${editId}`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ name }) });
    } else {
      await fetch(`${API}/api/categories`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ name }) });
    }
    setName(''); setEditId(null); fetchCats();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Izbriši kategorijo?')) return;
    await fetch(`${API}/api/categories/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${getToken()}` } });
    fetchCats();
  };

  const handleEdit = (cat) => { setEditId(cat.id); setName(cat.name); };

  return (
    <div className="min-h-screen bg-cream">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <Link to="/admin/dashboard" className="flex items-center gap-1 text-stone-600 hover:text-violet-700 text-sm mb-6"><ArrowLeft size={16} />Nazaj na nadzorno ploščo</Link>
        <h1 className="font-serif text-3xl text-stone-800 font-bold mb-6" data-testid="categories-title">Kategorije</h1>
        
        <div className="bg-white rounded-xl border border-stone-200 p-4 mb-6">
          {error && <p className="text-red-500 text-sm mb-2">{error}</p>}
          <div className="flex gap-2">
            <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Ime kategorije" className="flex-1 text-sm border border-stone-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-300" data-testid="category-input" />
            <Button onClick={handleSave} className="bg-violet-500 hover:bg-violet-600 text-white rounded-lg" data-testid="category-save">
              {editId ? <><Edit size={14} className="mr-1" />Posodobi</> : <><Plus size={14} className="mr-1" />Dodaj</>}
            </Button>
            {editId && <Button variant="outline" onClick={() => { setEditId(null); setName(''); }}>Prekliči</Button>}
          </div>
        </div>

        <div className="space-y-2">
          {categories.map(cat => (
            <div key={cat.id} className="bg-white rounded-xl border border-stone-100 px-4 py-3 flex items-center justify-between" data-testid={`category-${cat.id}`}>
              <div className="flex items-center gap-2">
                <Tag size={14} className="text-violet-500" />
                <span className="text-stone-800 text-sm font-medium">{cat.name}</span>
                <span className="text-stone-400 text-xs">/{cat.slug}</span>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => handleEdit(cat)} className="text-stone-500 hover:text-violet-600"><Edit size={14} /></Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(cat.id)} className="text-stone-500 hover:text-red-600"><Trash2 size={14} /></Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminCategories;
